import { useCallback } from "react";
import * as React from "react";
import {
  DeepMap,
  FieldError,
  FieldValues,
  UseControllerProps,
  useController
} from "react-hook-form";

import { grey } from "@mui/material/colors";
import type { SxProps } from "@mui/material/styles";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { get, merge } from "lodash";

import { isDateStringValid } from "utils/common";

type DateFormFieldProps = {
  name: string;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  inputFormat?: string;
  PopperProps?: any;
  sx?: SxProps;
  showBorder?: boolean;
  onChange?: (value: Date | null) => void;
};

export type RhfDateFormFieldProps<T extends FieldValues> = DateFormFieldProps &
  UseControllerProps<T>;

export default function DateFormField<T extends FieldValues>({
  name,
  label = "",
  disabled = false,
  control,
  inputFormat,
  required = false,
  PopperProps,
  sx = { ml: 2, mt: 1 },
  showBorder,
  onChange
}: RhfDateFormFieldProps<T>) {
  const validate = useCallback(
    (value: any) => {
      if (disabled) return true;
      if (required && !value) {
        return `${label} is required.`;
      }
      return !value || isDateStringValid(value) || `${label} is not valid.`;
    },
    [required, disabled, label]
  );
  const {
    field,
    formState: { errors }
  } = useController<T>({
    name,
    control,
    rules: {
      validate
    }
  });
  const errorMessage =
    get(errors, name) &&
    `${(get(errors, name) as DeepMap<FieldValues, FieldError>).message}`;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DesktopDatePicker
        views={["year", "month", "day"]}
        disabled={disabled}
        closeOnSelect
        {...(inputFormat ? { inputFormat: inputFormat } : {})}
        {...field}
        onChange={(value) => {
          if (isDateStringValid(value) || !value) {
            field.onChange(value);
            onChange?.(value);
          }
        }}
        value={field.value ? new Date(field.value) : null}
        slotProps={{
          popper: PopperProps,
          textField: {
            required,
            disabled,
            fullWidth: true,
            label,
            InputLabelProps: { disabled },
            InputProps: { disableUnderline: disabled },
            error: !disabled && Boolean(errorMessage),
            helperText: disabled ? "" : errorMessage || "",

            sx: merge(
              sx,
              showBorder && disabled
                ? { borderBottom: `solid 1px ${grey[500]}` }
                : {}
            )
          }
        }}
      />
    </LocalizationProvider>
  );
}
